import { useMemo } from 'react';
import { Table, Button, Space, Card, Tag, message, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { usePpeProcurement } from '@/entities/ppe/api/hooks';
import { antTablePagination, antTableScroll } from '@/shared/constants/antTable';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import type { PpeProcurementRow } from '@/entities/ppe/model/ppeSchema';
import {
  CSV_SUCCESS_MESSAGE,
  EXPORT_PDF_LABEL,
  EXPORT_R7_LABEL,
  PDF_ERROR_MESSAGE,
  PDF_SUCCESS_MESSAGE,
  PROCUREMENT_CARD_TITLE,
  PROCUREMENT_DEFICIT_TAG,
} from '../constants';
import { exportProcurementCsv, exportProcurementPdf, sumProcurementDeficit } from '../utils';
import s from './ProcurementTab.module.scss';

export function ProcurementTab() {
  const { data: procurement, isLoading: procurementLoading } = usePpeProcurement();

  const rows = procurement ?? [];
  const totalDeficit = sumProcurementDeficit(procurement);

  const procurementColumns: ColumnsType<PpeProcurementRow> = useMemo(
    () => [
      { title: 'Профессия', dataIndex: 'position', key: 'pos' },
      { title: 'СИЗ', dataIndex: 'ppeName', key: 'name' },
      { title: 'Норма', dataIndex: 'normQty', key: 'nq' },
      { title: 'Период', dataIndex: 'period', key: 'per' },
      { title: 'Сотр.', dataIndex: 'employeeCount', key: 'ec', width: 60 },
      { title: 'Потребность', dataIndex: 'totalNeed', key: 'need', width: 100 },
      { title: 'Выдано', dataIndex: 'issued', key: 'iss', width: 80 },
      {
        title: 'Дефицит',
        dataIndex: 'deficit',
        key: 'def',
        width: 80,
        render: (v: number) => <StatusBadge status={v > 0 ? 'err' : 'ok'} label={String(v)} />,
      },
    ],
    [],
  );

  return (
    <Card
      size="small"
      title={
        <span className={s['root__norms-card-title']}>
          {PROCUREMENT_CARD_TITLE}
          {totalDeficit > 0 && (
            <Tag color="error" style={{ marginLeft: 8 }}>
              {PROCUREMENT_DEFICIT_TAG(totalDeficit)}
            </Tag>
          )}
        </span>
      }
      extra={
        <Space>
          <Button
            onClick={() => {
              void exportProcurementPdf(rows)
                .then(() => message.success(PDF_SUCCESS_MESSAGE))
                .catch(() => message.error(PDF_ERROR_MESSAGE));
            }}
          >
            {EXPORT_PDF_LABEL}
          </Button>
          <Button
            onClick={() => {
              exportProcurementCsv(rows);
              message.success(CSV_SUCCESS_MESSAGE);
            }}
          >
            {EXPORT_R7_LABEL}
          </Button>
        </Space>
      }
    >
      <Spin spinning={procurementLoading}>
        <Table
          dataSource={procurement}
          columns={procurementColumns}
          rowKey={(r) => `${r.position}-${r.ppeName}`}
          size="small"
          pagination={{ ...antTablePagination }}
          scroll={antTableScroll}
        />
      </Spin>
    </Card>
  );
}
